import React, { FC, Dispatch, SetStateAction, useEffect, useCallback, useRef, useState } from 'react';

import cn from 'classnames';
import { useFormContext } from 'react-hook-form';

import {
  DueDiligencePersonsFormNamesEnum,
  DueDiligenceSections,
  SIGNIFICANT_CONTROL_BLOCK_DESCRIPTION,
  SIGNIFICANT_CONTROL_BLOCK_TITLE,
} from 'constants/due-diligence';
import useModal from 'hooks/use-modal/useModal';
import { UseDueDiligenceBusinessDetailsReturnValues } from 'hooks/useDueDiligenceBusinessDetails';
import useDueDiligencePersons from 'hooks/useDueDiligencePersons';
import { DueDiligencePersonsProps } from 'interfaces';
import { IsSCPSSameAsInSCCChecked, selectSignificantControlPersons } from 'modules/due-diligence/selectors';
import { useAppSelector } from 'modules/store';
import SignificantControlChildPersons from 'page-components/due-diligence/significant-control/SignificantControlChildPersons';

import { PersonsBlock } from '../../index';
import DueDiligenceBlock from '../due-diligence-block/DueDiligenceBlock';
import SignificantControlCompaniesTable from './SignificantControlCompaniesTable';

const SignificantControlBlock: FC<
  DueDiligencePersonsProps & {
    validateBlock: boolean;
    setValidateBlock: Dispatch<SetStateAction<boolean>>;
    isEditing: boolean;
    isConfirmationValidationHighlighted: boolean;
    isShownChildren?: boolean;
    businessDetailsProps: UseDueDiligenceBusinessDetailsReturnValues;
    setHasUnsavedSignificantControlBlock: Dispatch<SetStateAction<boolean>>;
    onParentSave?: (saveFn: () => Promise<boolean>) => Promise<boolean>;
  }
> = ({
  validateBlock,
  setValidateBlock,
  isEditing,
  isConfirmationValidationHighlighted,
  businessDetailsProps,
  setHasUnsavedSignificantControlBlock,
  onParentSave,
  ...personsProps
}) => {
  const significantControlPersons = useAppSelector(selectSignificantControlPersons);
  const isSCPSSameAsInSCCChecked = useAppSelector(IsSCPSSameAsInSCCChecked);
  const showPersons = !isSCPSSameAsInSCCChecked || !!significantControlPersons?.length;

  const {
    reset,
    handleSubmit,
    formState: { dirtyFields },
    getValues,
    watch,
  } = useFormContext();

  const {
    onSubmit,
    handleCancel,
    handleEdit,
    handleDiscardChanges,
    handleSetIsLoading,
    isLoading,
    setDeletedPersons,
    deletedPersons,
    deletedAddresses,
    setDeletedAddresses,
  } = useDueDiligencePersons({
    sectionType: DueDiligenceSections.SIGNIFICANT_CONTROL_PERSONS,
    reset: reset,
    dirtyFields: dirtyFields,
    name: 'directors',
    validateAddresses: validateBlock,
  });
  const onClickSaveRef = useRef(() => {
    // onClickSaveRef called without being set
  });

  // Add a ref to store the resolution function of our Promise
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  // Check if there are unsaved changes in dirtyFields.directors
  const hasUnsavedChanges = !!dirtyFields?.directors && Object.keys(dirtyFields.directors).length > 0;

  // Keep track of the previous value of hasUnsavedChanges
  const prevHasUnsavedChangesRef = useRef(hasUnsavedChanges);

  const modalProps = useModal();

  const watchDirectorsFieldsLength = watch(DueDiligencePersonsFormNamesEnum.DIRECTORS)?.length || 0;

  const onClickSave = useCallback(() => {
    if (validateBlock) {
      handleSubmit(onSubmit({ onOpen: modalProps.onOpen, handleCancel }))();
      return;
    }

    const values = getValues();
    onSubmit({ onOpen: modalProps.onOpen, handleCancel })(values);
  }, [validateBlock, handleSubmit, onSubmit, modalProps.onOpen, handleCancel, getValues]);

  const onClickDiscardChanges = () => {
    handleDiscardChanges();
    setValidateBlock(false);
  };

  // Update the ref whenever onClickSave changes
  useEffect(() => {
    onClickSaveRef.current = onClickSave;
  }, [onClickSave]);

  // Pass info about unsaved changes (submit button color change)
  useEffect(() => {
    setHasUnsavedSignificantControlBlock(hasUnsavedChanges);

    // If changes were saved and we have a pending promise resolution
    if (prevHasUnsavedChangesRef.current && !hasUnsavedChanges && resolveRef.current) {
      resolveRef.current(true);
      resolveRef.current = null;
    }

    prevHasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges, setHasUnsavedSignificantControlBlock]);

  // Allow opening save modal from this component in parent
  useEffect(() => {
    if (onParentSave) {
      const wrappedSave = () => {
        return new Promise<boolean>((resolve) => {
          if (!hasUnsavedChanges) {
            // If no changes, resolve immediately
            resolve(true);
            return;
          }

          // Store the resolve function to call later when changes are saved
          resolveRef.current = resolve;

          // Trigger the save
          onClickSaveRef.current();
        });
      };

      onParentSave(wrappedSave);
    }
  }, [onParentSave, hasUnsavedChanges]);

  // Add effect to handle modal closure (cancel case)
  useEffect(() => {
    const originalOnClose = modalProps.onClose;

    modalProps.onClose = () => {
      originalOnClose();

      // If modal was closed without saving, resolve with false
      if (resolveRef.current) {
        resolveRef.current(false);
        resolveRef.current = null;
      }
    };

    return () => {
      modalProps.onClose = originalOnClose;
    };
  }, [modalProps]);

  return (
    <DueDiligenceBlock
      blockNumber='1.2'
      descriptionClassName='text-grey-500'
      sectionType={DueDiligenceSections.SIGNIFICANT_CONTROL_PERSONS}
      title={SIGNIFICANT_CONTROL_BLOCK_TITLE}
      description={watchDirectorsFieldsLength > 2 ? SIGNIFICANT_CONTROL_BLOCK_DESCRIPTION : undefined}
      onClickEdit={handleEdit}
      onClickSave={showPersons ? onClickSave : undefined}
      onClickDiscardChanges={onClickDiscardChanges}
      isLoading={isLoading}
      hasUnsavedChanges={hasUnsavedChanges}
    >
      <SignificantControlCompaniesTable
        getUnfieldValue={personsProps.getUnfieldValue}
        isConfirmationValidationHighlighted={isConfirmationValidationHighlighted}
      />
      <SignificantControlChildPersons
        businessDetailsProps={businessDetailsProps}
        isEditing={isEditing}
        getUnfieldValue={personsProps.getUnfieldValue}
      />
      {showPersons && (
        <PersonsBlock
          {...personsProps}
          {...modalProps}
          personsList={significantControlPersons}
          name={DueDiligencePersonsFormNamesEnum.DIRECTORS}
          handleCancel={handleCancel}
          handleSetIsLoading={handleSetIsLoading}
          deletedPersons={deletedPersons}
          setDeletedPersons={setDeletedPersons}
          deletedAddresses={deletedAddresses}
          setDeletedAddresses={setDeletedAddresses}
          bypassValidation={!validateBlock}
          formClassName={cn({ 'disabled-light-grey': isLoading })}
          isEditing={isEditing}
          successMessage='The executive directors and persons were successfully saved'
        />
      )}
    </DueDiligenceBlock>
  );
};

export default SignificantControlBlock;
